"use client";

import { useCallback, useMemo, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useQueryClient } from "@tanstack/react-query";
import { Sheet, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { KbdShortcut } from "@/components/ui/kbd-shortcut";
import { Icon } from "@/components/ui/icon";
import { showToast } from "@/components/ui/sonner";
import { getApiErrorMessage } from "@/lib/api/error";
import { formatLongDate } from "@/lib/date/format-long-date";
import {
  bulkMove,
  getGetAtsPromotionPrecheckQueryKey,
  useAdvanceCandidate,
  useGetAtsPromotionPrecheck,
  useRejectCandidate,
} from "@/lib/api/generated";
import type { SingleCandidateActionResponse } from "@/lib/api/model";
import { ApiError } from "@/lib/api/error";
import { patchSyncedCandidatesQueued } from "@/features/jobs/lib/optimistic-queued-patch";
import { useProfileDetail } from "@/hooks/use-profile-detail";
import { Badge } from "@/components/ui/badge";
import { useProfileContacts } from "@/hooks/use-contact-reveal";
import {
  getOriginLabel,
  isEnrichOrigin,
  isImportOrigin,
  ProfileOrigin,
} from "@/components/ui/profile-origin";
import { AtsBadge } from "@/features/jobs/components/ats-badge";
import type { CandidateProfile, PipelineCandidate } from "@/lib/api/types";
import {
  MEETINGS_TAB,
  RESUME_TAB,
  SCORECARDS_TAB,
  type ProfileTab,
} from "@/features/candidate/lib/profile-constants";
import { CandidateScorecards } from "@/features/scorecards";
import { FEATURE_FLAGS } from "@/lib/feature-flags";
import { CandidateScope } from "@/features/conversations/components/scopes-embedded";
import { useConversationsFeature } from "@/features/conversations/hooks/use-conversations-feature";
import { Profile } from "@/features/candidate";
import {
  useProfile,
  useProfileSections,
} from "@/features/candidate/components/profile";
import { MoveToStage } from "./move-to-stage";
import { ResumeViewer } from "./resume-viewer";
import { ExportModal } from "@/features/search/components/actions/export";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { usePipelineJob } from "@/features/jobs/context/pipeline-job-context";
import ConfigureModal from "@/features/enrollment/components/configure-modal";
import { useConfigureStore } from "@/features/enrollment/store/configure-store";
import { useJobAtsLink } from "@/features/ats-sync/hooks/use-job-ats-link";
import {
  WontSyncMarker,
  SourcedSyncHint,
} from "@/features/ats-sync/components/wont-sync-marker";
import {
  SyncStateBadge,
  isSyncActionEnabled,
  syncDisabledReason,
} from "@/features/ats-sync/components/sync/sync-state-badge";
import { useSyncFlow } from "@/features/ats-sync/components/sync/use-sync-flow";
import { ApplicationAnswersSheet } from "@/features/ats-sync/components/sync/application-answers-sheet";
import { RejectInAtsModal } from "@/features/ats-sync/components/reject-in-ats-modal";
import { useRejectionReasons } from "@/features/ats-sync/hooks/use-rejection-reasons";
import { rejectAvailability } from "@/features/ats-sync/lib/gating";
import { invalidateSyncPreflight } from "@/features/ats-sync/lib/invalidate-after-sync";
import { getArchiveStage } from "./archive-stage";
import { nextStageId } from "./next-stage";
import { usePipelineProfileExport } from "./export-jobs";

interface PipelineProfileProps {
  profileId: string | null;
  profileIds: string[];
  open: boolean;
  onClose: () => void;
  onNavigate: (profileId: string) => void;
  candidates?: PipelineCandidate[];
  selectedStageId?: string | null;
}

export function PipelineProfile({
  profileId,
  profileIds,
  open,
  onClose,
  onNavigate,
  candidates,
  selectedStageId,
}: PipelineProfileProps) {
  const { job, stages, onRefresh } = usePipelineJob();
  const queryClient = useQueryClient();
  const conversationsEnabled = useConversationsFeature();
  const { data: profile, isLoading: profileLoading } =
    useProfileDetail(profileId);
  const { isLoading: contactsLoading } = useProfileContacts(profileId);
  const isLoading = profileLoading || contactsLoading;
  const [activeTab, setActiveTab] = useState<ProfileTab>("profile");
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [enrollModalOpen, setEnrollModalOpen] = useState(false);
  const setConfigure = useConfigureStore((s) => s.set);
  const [moveLoading, setMoveLoading] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);

  const isViewingAll =
    selectedStageId === null || selectedStageId === undefined;

  const currentIndex = profileId ? profileIds.indexOf(profileId) : -1;

  const candidate = useMemo(
    () => candidates?.find((c) => c.profile_id === profileId) ?? null,
    [candidates, profileId],
  );

  const candidateStage = useMemo(() => {
    if (!candidate?.stage_id || !stages) return null;
    return stages.find((s) => s.id === candidate.stage_id) ?? null;
  }, [candidate, stages]);

  const sortedStages = useMemo(
    () => (stages ? [...stages].sort((a, b) => a.position - b.position) : []),
    [stages],
  );

  const archivedStage = useMemo(
    () => getArchiveStage(sortedStages),
    [sortedStages],
  );

  const nextStage = useMemo(() => {
    if (!candidateStage) return null;
    const id = nextStageId(
      sortedStages,
      candidateStage.id,
      archivedStage?.id ?? null,
    );
    return id ? (sortedStages.find((s) => s.id === id) ?? null) : null;
  }, [candidateStage, sortedStages, archivedStage]);

  const isArchived = candidateStage?.id === archivedStage?.id;
  const canAdvance = !!nextStage;
  const canReject = !isArchived && !!archivedStage;

  // ATS sync: link state for this job
  const {
    isLinked,
    connectionId,
    connectorName: ats,
    capabilitiesSnapshot: snapshot,
    dormant,
    syncDirection,
  } = useJobAtsLink(job.id);
  // A dormant (unlinked-but-preserved) link still freezes a candidate's last
  // known sync state — the badge renders it as "Paused" even though isLinked
  // is false while dormant.
  const paused = dormant != null;

  // Sourced = first stage in sorted order; sync only begins on promotion out of it
  const sourcedStage = sortedStages.length > 0 ? sortedStages[0] : null;
  const isSourced = candidateStage?.id === sourcedStage?.id;

  // Sync action eligibility keys off the SYSTEM Sourced stage (is_system &&
  // position === 0), never a name match. The badge supersedes the old
  // ats_application_linked indicator; the action opens the shared sync flow.
  const { open: openSyncFlow } = useSyncFlow();
  const syncState = candidate?.ats_sync_state;
  const syncError = candidate?.ats_sync_error;
  const syncPendingAction = candidate?.ats_sync_pending_action;
  const isInSystemSourced =
    !!candidateStage?.is_system && candidateStage?.position === 0;
  const syncEnabled =
    isLinked &&
    isSyncActionEnabled(syncState, isInSystemSourced, syncDirection);
  const syncDisabledTitle = isLinked
    ? syncDisabledReason(syncState, isInSystemSourced, syncDirection)
    : null;
  const handleSync = useCallback(() => {
    if (!candidate || !syncEnabled) return;
    openSyncFlow({
      jobId: job.id,
      jobCandidateIds: [candidate.job_candidate_id],
      names: candidate.full_name
        ? { [candidate.job_candidate_id]: candidate.full_name }
        : undefined,
      onDone: () => onRefresh?.(),
    });
  }, [candidate, syncEnabled, openSyncFlow, job.id, onRefresh]);

  const [rejectOpen, setRejectOpen] = useState(false);
  const [answersSheetOpen, setAnswersSheetOpen] = useState(false);
  const { hasReasons } = useRejectionReasons(connectionId, isLinked);
  const rejectGate = isLinked
    ? rejectAvailability(snapshot, ats, hasReasons)
    : { allowed: false, reason: null };

  // Ashby-only (#20 partial): linked + not yet pushed; same sheet as sync, no apply-to-all.
  const showApplicationAnswersEntry =
    isLinked &&
    !!candidate &&
    !candidate.ats_application_linked &&
    ats.trim().toLowerCase() === "ashby";

  // Promotion precheck drives the *visible* Fill answers banner: when the ATS
  // reports outstanding application questions, surface a prominent CTA instead
  // of leaving the answers entry buried in the action list.
  const precheck = useGetAtsPromotionPrecheck(
    job.id,
    candidate?.job_candidate_id ?? "",
    {
      query: {
        enabled: showApplicationAnswersEntry && !!candidate?.job_candidate_id,
      },
    },
  );
  const applicationQuestionsIncomplete =
    showApplicationAnswersEntry &&
    precheck.data?.application_questions_incomplete === true;
  const applicationQuestionsSoftHint =
    showApplicationAnswersEntry &&
    precheck.data?.application_questions_soft_hint === true;
  const showApplicationAnswersBanner =
    applicationQuestionsIncomplete || applicationQuestionsSoftHint;

  const navigateAfterAction = useCallback(() => {
    if (isViewingAll) return;
    const nextIdx = currentIndex + 1;
    const prevIdx = currentIndex - 1;
    if (nextIdx < profileIds.length) {
      onNavigate(profileIds[nextIdx]);
    } else if (prevIdx >= 0) {
      onNavigate(profileIds[prevIdx]);
    } else {
      onClose();
    }
  }, [isViewingAll, currentIndex, profileIds, onNavigate, onClose]);

  const advanceMutation = useAdvanceCandidate({
    mutation: {
      onSuccess: (
        data: SingleCandidateActionResponse,
        variables: { jobId: string; data: { job_candidate_id: string } },
      ) => {
        // Auto-sync now runs in the background after advance — the response no
        // longer carries auto_sync counts; show the stage move toast only.
        const actionMsg = `Advanced to ${data.target_stage_name}`;
        showToast.success(actionMsg);
        patchSyncedCandidatesQueued(
          queryClient,
          job.id,
          [variables?.data?.job_candidate_id].filter((id): id is string =>
            Boolean(id),
          ),
          "stage_change",
        );
        onRefresh?.();
        navigateAfterAction();
        invalidateSyncPreflight(queryClient, job.id);
      },
      onError: (err: unknown) => {
        // A re-link reconciliation can leave a candidate without a stage
        // (its stage was removed remotely) — advancing then 409s with
        // CANDIDATE_UNSTAGED since there's no current stage to advance from.
        // Surface a friendly nudge toward the needs-stage tray instead of
        // the generic failure toast.
        if (err instanceof ApiError && err.code === "CANDIDATE_UNSTAGED") {
          showToast.error("Assign a stage first");
          onRefresh?.();
          return;
        }
        showToast.error("Failed to advance candidate", {
          description: getApiErrorMessage(err),
        });
        // No optimistic patch on failure — bust so any stale UI settles.
        onRefresh?.();
      },
    },
  });

  const rejectMutation = useRejectCandidate({
    mutation: {
      onSuccess: (data: SingleCandidateActionResponse) => {
        showToast.success(`Moved to ${data.target_stage_name}`);
        onRefresh?.();
        navigateAfterAction();
        invalidateSyncPreflight(queryClient, job.id);
      },
      onError: (err) =>
        showToast.error("Failed to reject candidate", {
          description: getApiErrorMessage(err),
        }),
    },
  });

  const handleAdvance = useCallback(() => {
    if (!canAdvance || !candidate) return;
    advanceMutation.mutate({
      jobId: job.id,
      data: { job_candidate_id: candidate.job_candidate_id },
    });
  }, [canAdvance, candidate, job.id, advanceMutation]);

  const handleReject = useCallback(() => {
    if (!canReject || !candidate) return;
    if (isLinked && candidate.ats_application_linked) {
      // Linked AND pushed to the ATS: archive via the required-reason modal.
      setRejectOpen(true);
      return;
    }
    // Unlinked job, or linked job but candidate not yet pushed (no application
    // link): archive locally — the backend guard permits unlinked archives.
    rejectMutation.mutate({
      jobId: job.id,
      data: { job_candidate_id: candidate.job_candidate_id },
    });
  }, [canReject, candidate, isLinked, job.id, rejectMutation]);

  const handleMoveToStage = useCallback(
    async (stageId: string) => {
      if (!candidate) return;
      if (
        archivedStage &&
        stageId === archivedStage.id &&
        isLinked &&
        candidate.ats_application_linked
      ) {
        // Route archive-via-move through the required-reason modal (spec F3),
        // same as the Reject button.
        setMoveModalOpen(false);
        setRejectOpen(true);
        return;
      }
      setMoveLoading(true);
      try {
        await bulkMove(job.id, {
          job_candidate_ids: [candidate.job_candidate_id],
          target_stage_id: stageId,
        });
        const stageName =
          sortedStages.find((s) => s.id === stageId)?.name ?? "stage";
        // Stage moves never gate: surface any ATS auto-sync outcome, merged
        // into one toast with the move's own success message when it fired.
        const actionMsg = `Moved to ${stageName}`;
        showToast.success(actionMsg);
        setMoveModalOpen(false);
        patchSyncedCandidatesQueued(
          queryClient,
          job.id,
          [candidate.job_candidate_id],
          "stage_change",
        );
        onRefresh?.();
        navigateAfterAction();
        invalidateSyncPreflight(queryClient, job.id);
      } catch (err) {
        showToast.error("Failed to move candidate", {
          description: getApiErrorMessage(err),
        });
        onRefresh?.();
      } finally {
        setMoveLoading(false);
      }
    },
    [
      job.id,
      candidate,
      sortedStages,
      onRefresh,
      navigateAfterAction,
      archivedStage,
      isLinked,
      ats,
      queryClient,
    ],
  );

  const handleAddToSequence = useCallback(() => {
    if (!candidate) return;
    // Single-candidate cohort — seed the configure store and open the
    // Configure modal. The review drawer is mounted once globally in the
    // dashboard layout, so we only mount the modal here.
    setConfigure({
      jobId: job.id,
      jobCandidateIds: [candidate.job_candidate_id],
      sequenceId: null,
    });
    setEnrollModalOpen(true);
  }, [candidate, job.id, setConfigure]);

  const profileExport = usePipelineProfileExport(candidate);

  const isMutating = advanceMutation.isPending || rejectMutation.isPending;
  const rejectBlockedByGate =
    isLinked && candidate?.ats_application_linked && !rejectGate.allowed;
  const rejectDisabled = !canReject || isMutating || rejectBlockedByGate;
  const rejectDisabledReason = rejectBlockedByGate ? rejectGate.reason : null;

  useHotkeys(
    "a",
    (e) => {
      e.preventDefault();
      handleAdvance();
    },
    { enabled: open && canAdvance && !isMutating },
    [open, canAdvance, isMutating, handleAdvance],
  );

  useHotkeys(
    "r",
    (e) => {
      e.preventDefault();
      handleReject();
    },
    { enabled: open && canReject && !isMutating },
    [open, canReject, isMutating, handleReject],
  );

  const handleNavigate = useCallback(
    (direction: "prev" | "next") => {
      if (currentIndex === -1) return;
      const nextIdx =
        direction === "prev" ? currentIndex - 1 : currentIndex + 1;
      if (nextIdx >= 0 && nextIdx < profileIds.length) {
        onNavigate(profileIds[nextIdx]);
      }
    },
    [currentIndex, profileIds, onNavigate],
  );

  const originLabel = getOriginLabel(candidate?.origin);
  // Resume tab: shown for origins whose resume file is stored on our side —
  // manual uploads (upload_*) and ATS-imported candidates (the attachment
  // sync pulls the ATS resume into storage; same resume-url endpoint serves
  // both, and the viewer shows a friendly empty state when the ATS had none).
  const hasResumeTab =
    isImportOrigin(candidate?.origin) || candidate?.origin === "ats";

  const addedDate = formatLongDate(candidate?.added_at);

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      size="profile"
    >
      <SheetTitle className="sr-only">Candidate profile</SheetTitle>
      <SheetHeader className="relative w-full border-solid px-4 md:px-8">
        <div className="relative flex min-w-0 items-center gap-3 md:shrink-0">
          <div className="relative flex shrink-0 items-center gap-3">
            <Profile.NavIconButton
              aria-label="Previous profile"
              onClick={() => handleNavigate("prev")}
              disabled={currentIndex <= 0}
            >
              <Icon
                name="IconChevronLeft"
                className="size-3.5 text-muted-foreground"
              />
            </Profile.NavIconButton>
            <Profile.NavIconButton
              aria-label="Next profile"
              onClick={() => handleNavigate("next")}
              disabled={currentIndex >= profileIds.length - 1}
            >
              <Icon
                name="IconChevronRight"
                className="size-3.5 text-muted-foreground"
              />
            </Profile.NavIconButton>
          </div>
          <p className="relative min-w-0 truncate text-sm font-normal leading-5 text-contrast/50 md:shrink-0 md:overflow-visible md:whitespace-nowrap">
            {currentIndex + 1} of {profileIds.length} profiles
          </p>
        </div>
        <Profile.NavIconButton aria-label="Close profile" onClick={onClose}>
          <Icon
            name="IconCrossMedium"
            className="size-3.5 text-muted-foreground"
          />
        </Profile.NavIconButton>
      </SheetHeader>

      {isLoading || !profile ? (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <Profile.Skeleton variant="pipeline" />
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-surface-elevated">
          <Profile.Layout profile={profile} profileId={profileId}>
            {/* Profile header - inside Layout so useProfile() context is available */}
            <Profile.Layout.Header>
              <Profile.Header
                actions={
                  <div className="hidden md:flex items-center gap-3 shrink-0">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          aria-label="More actions"
                          className="flex size-7 items-center justify-center rounded-[6px] bg-contrast/4 border border-contrast/[0.15] hover:bg-contrast/8 transition-colors cursor-pointer shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]"
                        >
                          <Icon
                            name="IconDotGrid1x3Horizontal"
                            size={16}
                            className="text-foreground"
                          />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" sideOffset={6}>
                        <DropdownMenuItem
                          onClick={() => setExportModalOpen(true)}
                          disabled={profileExport.exporting}
                        >
                          <Icon
                            name="IconFileDownload"
                            size={16}
                            className="text-foreground opacity-60"
                          />
                          Export Profile
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <div className="w-px h-[25px] bg-contrast/10" />
                    <div className="flex items-center">
                      <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        className="rounded-r-none border-r-0"
                        onClick={handleAddToSequence}
                        disabled={!candidate}
                      >
                        <Icon name="IconPaperPlane" size={16} />
                        Add to Sequence
                      </Button>
                      <button
                        type="button"
                        className="flex h-7 items-center justify-center rounded-r-[6px] bg-primary hover:bg-primary/90 border border-primary-foreground/15 border-l-primary-foreground/20 px-1.5 transition-colors cursor-pointer shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]"
                      >
                        <Icon
                          name="IconChevronDownSmall"
                          size={16}
                          className="text-primary-foreground"
                        />
                      </button>
                    </div>
                  </div>
                }
                mobileMenuItems={
                  <DropdownMenuItem
                    onClick={() => setExportModalOpen(true)}
                    disabled={profileExport.exporting}
                  >
                    <Icon
                      name="IconFileDownload"
                      size={16}
                      className="text-foreground opacity-60"
                    />
                    Export Profile
                  </DropdownMenuItem>
                }
                nameBadge={
                  candidateStage ? (
                    <>
                      <Badge
                        variant="linear"
                        size="sm"
                        className="h-[26px] rounded-[6px] text-foreground"
                        style={
                          {
                            "--gradient-color":
                              candidateStage.color ?? "#6b7280",
                          } as React.CSSProperties
                        }
                      >
                        {candidateStage.name}
                      </Badge>
                      {isLinked && !isSourced && (
                        <WontSyncMarker
                          snapshot={snapshot}
                          ats={ats}
                          className="h-[26px] rounded-[6px]"
                        />
                      )}
                      {(isLinked || paused) && (
                        <SyncStateBadge
                          state={syncState}
                          error={syncError}
                          pendingAction={syncPendingAction}
                          direction={syncDirection}
                          paused={paused}
                          className="h-[26px] rounded-[6px] px-1.5 text-foreground"
                        />
                      )}
                    </>
                  ) : null
                }
                extraRows={
                  <div className="flex flex-wrap items-start gap-3 md:gap-6">
                    {originLabel && (
                      <div className="flex flex-col gap-0.5">
                        <span className="text-[13px] text-contrast/60">
                          Profile Origin
                        </span>
                        {candidate?.origin === "ats" && job.ats_link ? (
                          <AtsBadge
                            link={job.ats_link}
                            href={candidate.ats_remote_url}
                          />
                        ) : (
                          <ProfileOrigin origin={candidate?.origin} />
                        )}
                      </div>
                    )}
                    {addedDate && (
                      <div className="flex flex-col gap-0.5">
                        <span className="text-[13px] text-contrast/60">
                          Added at
                        </span>
                        <span className="text-sm text-heading">
                          {addedDate}
                        </span>
                      </div>
                    )}
                    {job.title && (
                      <div className="flex flex-col gap-0.5">
                        <span className="text-[13px] text-contrast/60">
                          Job Requisition
                        </span>
                        <span className="text-sm text-heading">
                          {job.title}
                        </span>
                      </div>
                    )}
                    {isLinked && isSourced && <SourcedSyncHint ats={ats} />}
                  </div>
                }
              />

              {/* Mobile action strip */}
              <div className="flex md:hidden shrink-0 items-center gap-1.5 border-b border-border bg-surface-header px-4 py-3">
                <Button
                  type="button"
                  variant="success-subtle"
                  size="sm"
                  onClick={handleAdvance}
                  disabled={!canAdvance || isMutating}
                  className="min-w-0 flex-1"
                  icon={
                    <Icon
                      name={
                        advanceMutation.isPending
                          ? "IconLoadingCircle"
                          : "IconCircleCheck"
                      }
                      size={14}
                      className={
                        advanceMutation.isPending ? "animate-spin" : undefined
                      }
                    />
                  }
                >
                  Advance
                </Button>
                <Button
                  type="button"
                  variant="destructive-subtle"
                  size="sm"
                  onClick={handleReject}
                  disabled={rejectDisabled}
                  title={rejectDisabledReason ?? undefined}
                  className="min-w-0 flex-1"
                  icon={
                    <Icon
                      name={
                        rejectMutation.isPending
                          ? "IconLoadingCircle"
                          : "IconCircleX"
                      }
                      size={14}
                      className={
                        rejectMutation.isPending ? "animate-spin" : undefined
                      }
                    />
                  }
                >
                  Reject
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => setMoveModalOpen(true)}
                  disabled={isMutating}
                  className="min-w-0 flex-1"
                  icon={
                    <Icon
                      name="IconPlan"
                      size={14}
                      className="text-foreground"
                    />
                  }
                >
                  Move
                </Button>
              </div>
            </Profile.Layout.Header>

            <Profile.Layout.Content>
              <Profile.ScrollArea constrainContentWidth>
                {/* Mobile-only: pin AI Spotlight + Highlights above the tabs */}
                <Profile.MobileSidebar className="border-t-0">
                  <Profile.Spotlight />
                  <Profile.Highlights profile={profile} />
                </Profile.MobileSidebar>
                <Profile.TabBar>
                  <Profile.Tabs
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                    extraTabs={[...(hasResumeTab ? [RESUME_TAB] : []), ...(FEATURE_FLAGS.scorecards && candidate?.job_candidate_id ? [SCORECARDS_TAB] : []), ...(conversationsEnabled && candidate?.job_candidate_id ? [MEETINGS_TAB] : [])]}
                  />
                </Profile.TabBar>
                {activeTab === "profile" && (
                  <PipelineProfileSections
                    profile={profile}
                    revealAction={
                      isEnrichOrigin(candidate?.origin) ? "enrich" : "reveal"
                    }
                  />
                )}
                {activeTab === "jobs" && profileId && (
                  <Profile.SectionStack>
                    <Profile.Section.Jobs profileId={profileId} />
                  </Profile.SectionStack>
                )}
                {activeTab === "resume" &&
                  hasResumeTab &&
                  candidate?.job_candidate_id && (
                    <Profile.PagePadding>
                      <ResumeViewer
                        jobId={job.id}
                        jobCandidateId={candidate.job_candidate_id}
                      />
                    </Profile.PagePadding>
                  )}
                {FEATURE_FLAGS.scorecards && activeTab === "scorecards" && candidate?.job_candidate_id && (
                  <Profile.PagePadding>
                    <CandidateScorecards jobId={job.id} jcId={candidate.job_candidate_id} />
                  </Profile.PagePadding>
                )}
                {activeTab === "conversations" && candidate?.job_candidate_id && (
                  <Profile.PagePadding>
                    <CandidateScope jobCandidateId={candidate.job_candidate_id} />
                  </Profile.PagePadding>
                )}
                {activeTab !== "profile" &&
                  activeTab !== "jobs" &&
                  activeTab !== "resume" &&
                  activeTab !== "scorecards" &&
                  activeTab !== "conversations" && <Profile.EmptyTabPlaceholder />}

                {/* Mobile-only: Notes after content */}
                <Profile.MobileSidebar>
                  <Profile.JobNotes jobId={job.id} profileId={profileId} />
                </Profile.MobileSidebar>
              </Profile.ScrollArea>
            </Profile.Layout.Content>
            <Profile.Layout.Sidebar>
              <Profile.SidebarColumn>
                <div className="flex flex-col gap-2.5 border-b border-border px-8 py-6">
                  {showApplicationAnswersBanner && (
                    <div className="flex flex-col gap-2 rounded-md border border-warning/30 bg-warning/5 px-3 py-2.5">
                      <div className="flex items-start gap-2">
                        <Icon
                          name="IconExclamationCircle"
                          size={16}
                          className="mt-0.5 shrink-0 text-warning"
                        />
                        <p className="text-xs leading-4 text-heading">
                          {applicationQuestionsIncomplete
                            ? `Application answers needed before syncing to ${ats}.`
                            : "This job has application questions — fill in Arbi if you want them on the ATS application"}
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        onClick={() => setAnswersSheetOpen(true)}
                        disabled={isMutating}
                        className="w-full rounded-sm"
                        icon={<Icon name="IconFileText" size={16} />}
                      >
                        Fill answers
                      </Button>
                    </div>
                  )}
                  <div className="flex items-start gap-3">
                    <Button
                      type="button"
                      variant="success-subtle"
                      size="sm"
                      onClick={handleAdvance}
                      disabled={!canAdvance || isMutating}
                      className="flex-1 rounded-sm"
                      icon={
                        <Icon
                          name={
                            advanceMutation.isPending
                              ? "IconLoadingCircle"
                              : "IconCircleCheck"
                          }
                          size={16}
                          className={
                            advanceMutation.isPending
                              ? "animate-spin"
                              : undefined
                          }
                        />
                      }
                    >
                      Advance
                      <KbdShortcut
                        keys={["A"]}
                        size="icon-xs"
                        variant="primary"
                      />
                    </Button>
                    <Button
                      type="button"
                      variant="destructive-subtle"
                      size="sm"
                      onClick={handleReject}
                      disabled={rejectDisabled}
                      title={rejectDisabledReason ?? undefined}
                      className="flex-1 rounded-sm"
                      icon={
                        <Icon
                          name={
                            rejectMutation.isPending
                              ? "IconLoadingCircle"
                              : "IconCircleX"
                          }
                          size={16}
                          className={
                            rejectMutation.isPending
                              ? "animate-spin"
                              : undefined
                          }
                        />
                      }
                    >
                      Reject
                      <KbdShortcut
                        keys={["R"]}
                        size="icon-xs"
                        variant="primary"
                      />
                    </Button>
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => setMoveModalOpen(true)}
                    disabled={isMutating}
                    className="w-full rounded-sm"
                    icon={
                      <Icon
                        name="IconPlan"
                        size={16}
                        className="text-foreground"
                      />
                    }
                  >
                    Move to Stage
                  </Button>
                  {isLinked && (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={handleSync}
                      disabled={!syncEnabled || isMutating}
                      title={syncDisabledTitle ?? undefined}
                      className="w-full rounded-sm"
                      icon={
                        <Icon
                          name="IconArrowsRepeat"
                          size={16}
                          className="text-foreground"
                        />
                      }
                    >
                      Sync with ATS
                    </Button>
                  )}
                  {showApplicationAnswersEntry && (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => setAnswersSheetOpen(true)}
                      disabled={isMutating}
                      className="w-full rounded-sm"
                      icon={
                        <Icon
                          name="IconFileText"
                          size={16}
                          className="text-foreground"
                        />
                      }
                    >
                      Application answers
                    </Button>
                  )}
                  {profile?.ats_resume_parse_outcome === "imported" && (
                    <p className="text-xs text-contrast/50 leading-4">
                      History imported from resume
                    </p>
                  )}
                </div>

                <Profile.Spotlight />
                <Profile.Highlights profile={profile} />
                <Profile.JobNotes jobId={job.id} profileId={profileId} />
              </Profile.SidebarColumn>
            </Profile.Layout.Sidebar>
          </Profile.Layout>
        </div>
      )}
      <MoveToStage
        open={moveModalOpen}
        onOpenChange={setMoveModalOpen}
        jobId={job.id}
        onConfirm={handleMoveToStage}
        loading={moveLoading}
        count={1}
      />
      {/* Controlled mount: the modal keeps its base-ui root mounted so closing
          (X / Esc / outside-press) runs the dialog's close lifecycle. The shell
          and its sequence/estimate queries only mount while open (handled inside
          ConfigureModal). The review drawer is mounted once globally in the
          dashboard layout. */}
      <ConfigureModal
        open={enrollModalOpen}
        onOpenChange={setEnrollModalOpen}
      />
      {isLinked && connectionId && rejectOpen && (
        // Mount only while open (mirrors index.tsx) so the modal's internal
        // reason/note state is fresh per candidate instead of persisting from
        // the previously rejected candidate.
        <RejectInAtsModal
          open
          onOpenChange={(o) => !o && setRejectOpen(false)}
          connectionId={connectionId}
          candidateIds={[candidate?.job_candidate_id ?? ""]}
          snapshot={snapshot}
          ats={ats}
          onRejected={() => {
            setRejectOpen(false);
            onRefresh?.();
          }}
        />
      )}
      {showApplicationAnswersEntry && candidate ? (
        <ApplicationAnswersSheet
          open={answersSheetOpen}
          onOpenChange={setAnswersSheetOpen}
          jobId={job.id}
          jobCandidateId={candidate.job_candidate_id}
          candidateName={candidate.full_name ?? undefined}
          onSaved={() => {
            invalidateSyncPreflight(queryClient, job.id);
            queryClient.invalidateQueries({
              queryKey: getGetAtsPromotionPrecheckQueryKey(
                job.id,
                candidate.job_candidate_id,
              ),
            });
            onRefresh?.();
          }}
        />
      ) : null}
      <ExportModal
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        profileCount={1}
        source={profileExport.source}
        onExport={profileExport.startExport}
      />
    </Sheet>
  );
}

interface PipelineProfileSectionsProps {
  profile: CandidateProfile;
  revealAction: "reveal" | "enrich";
}

function PipelineProfileSections({
  profile,
  revealAction,
}: PipelineProfileSectionsProps) {
  const sections = useProfileSections();
  const { derived } = useProfile();
  return (
    <Profile.SectionStack>
      <Profile.Section.PersonalInfo
        profile={profile}
        revealAction={revealAction}
      />
      <Profile.Section.About summary={profile.summary ?? null} />
      <Profile.Section.Experience profile={profile} />
      <Profile.Section.Education education={sections.education} />
      <Profile.Section.Certifications
        certifications={sections.certifications}
      />
      <Profile.Section.Skills
        skills={profile.skills ?? []}
        firstName={derived.firstName}
      />
      <Profile.Section.Languages languages={sections.languages} />
    </Profile.SectionStack>
  );
}
